// MF-554 — Read-only view of a published Board (the Analytics button of engine 'canvas').
// 12-column bento grid with fixed-height rows (D7); on narrow screens tiles stack in reading order.
// A parent picks which child to look at; the backend scopes every tile to that child (and refuses
// anyone else's), a student always sees only themselves.
import { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { Box, Button, MenuItem, Skeleton, TextField, Typography, useMediaQuery } from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faChartBar, faLock } from '@fortawesome/pro-light-svg-icons'
import { compactTiles } from './Builder/layout'
import { getBoardData, getBoardView } from '../../../../services/boards/boards.service'
import BoardTile from './BoardTile'
import { GRID_GAP, ROW_HEIGHT, SUMMARY_PALETTE, fmtAsOf } from './boardTheme'
import StudentSelect from './StudentSelect'
import { BoardStyleContext } from './boardPalettes'

const byReadingOrder = (a, b) => a.y - b.y || a.x - b.x

const BoardViewer = ({ code }) => {
  const profile = useSelector((state) => state?.session?.profile)
  const roleIds = (profile?.roles || []).map((r) => r.id)
  const isParentOnly = roleIds.length > 0 && roleIds.every((id) => id === 'Parent')
  const isStudentOnly = roleIds.length > 0 && roleIds.every((id) => id === 'Student')
  // Staff may look at the whole school or pick one student; students and parents only see their own.
  const isStaff = !isParentOnly && !isStudentOnly
  const children = useMemo(() => (isParentOnly ? profile?.children || [] : []), [isParentOnly, profile])
  const stacked = useMediaQuery('(max-width: 900px)')
  const navigate = useNavigate()
  // Builders get a way back to the editor from the published Board.
  const canBuild = !!useSelector((state) => state?.session?.permissions?.boardsbuilder)

  const [board, setBoard] = useState(null)
  const [error, setError] = useState(null)
  const [childId, setChildId] = useState('')
  const [pickedStudent, setPickedStudent] = useState(null) // staff: { code, label } from the roster
  const [results, setResults] = useState(null) // { [tileId]: { loading, data, error } }

  useEffect(() => {
    let alive = true
    setBoard(null)
    setError(null)
    getBoardView(code)
      .then((b) => alive && setBoard(b))
      .catch((err) => alive && setError({ status: err?.status || 0 }))
    return () => {
      alive = false
    }
  }, [code])

  // A parent with one child sees them straight away.
  useEffect(() => {
    if (isParentOnly && !childId && children.length === 1) setChildId(String(children[0]._id))
  }, [isParentOnly, childId, children])

  // Every tile's data in ONE request (a parent: for the chosen child).
  useEffect(() => {
    if (!board || (isParentOnly && !childId)) return undefined
    // MF-574 — a student dashboard shows nothing until staff pick the student (never the school's numbers)
    if (isStaff && board.settings?.studentFirst && !pickedStudent) return undefined
    let alive = true
    setResults(null)
    getBoardData(
      board.code,
      childId ? { studentId: childId } : pickedStudent ? { studentCode: pickedStudent.code } : {}
    )
      .then((d) => {
        if (!alive) return
        const out = {}
        for (const [id, r] of Object.entries(d?.tiles || {})) {
          out[id] = {
            loading: false,
            data: r.data || null,
            error: r.error
              ? typeof r.error === 'string'
                ? r.error
                : r.error?.message || 'This tile could not be loaded.'
              : null
          }
        }
        setResults(out)
      })
      .catch((err) => {
        if (!alive) return
        const out = {}
        for (const t of board.tiles || []) {
          out[t.id] = { loading: false, data: null, error: err?.message || 'This tile could not be loaded.' }
        }
        setResults(out)
      })
    return () => {
      alive = false
    }
  }, [board, childId, isParentOnly, pickedStudent, isStaff])
  const asOf = useMemo(() => {
    const dates = Object.values(results || {})
      .map((r) => r?.data?.meta?.dataAsOf)
      .filter(Boolean)
      .sort()
    return dates.length ? fmtAsOf(dates[0]) : null // the oldest source is the honest "as of"
  }, [results])

  if (error) {
    // 404: it does not exist or is not shared with you; 401/403: no access; anything else: could not load
    const missing = error.status === 404
    const denied = error.status === 401 || error.status === 403
    return (
      <Box
        sx={{
          minHeight: 360,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          p: 4
        }}
      >
        <Box sx={{ maxWidth: 460 }}>
          <Box
            sx={{
              width: 112,
              height: 112,
              mx: 'auto',
              mb: 2.5,
              borderRadius: '50%',
              bgcolor: '#e3ebf6',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <FontAwesomeIcon icon={denied ? faLock : faChartBar} style={{ fontSize: 52, color: '#2A69A7' }} />
          </Box>
          <Typography sx={{ fontSize: 20, fontWeight: 700, color: SUMMARY_PALETTE.ink, mb: 0.75 }}>
            {missing
              ? 'This board is not available to you'
              : denied
                ? 'You do not have access to this board'
                : 'This board could not be loaded'}
          </Typography>
          <Typography sx={{ fontSize: 13, color: SUMMARY_PALETTE.muted, lineHeight: 1.5 }}>
            {missing
              ? 'It may have been unpublished, or it is not shared with your role. Ask the person who built it to share it with you.'
              : denied
                ? 'Your account does not include Boards right now. If you were just given access, sign out and in again.'
                : 'Something went wrong while loading it. Try again in a moment; if it keeps happening, tell the team.'}
          </Typography>
        </Box>
      </Box>
    )
  }
  if (!board) {
    return (
      <Box sx={{ p: 3 }}>
        <Skeleton width={260} height={36} />
        <Skeleton variant='rounded' height={240} sx={{ mt: 2, borderRadius: '14px' }} />
      </Box>
    )
  }

  // Never overlap, even if a stored layout does (MF-554 F4).
  const allTiles = compactTiles(board.tiles || []).sort(byReadingOrder)
  // MF-574 — a panel with no data is not shown at all (no "nothing here" message): once the data is in, the empty
  // ones leave and the rest move up to close the gap. A panel that FAILED stays, so a real problem is never hidden.
  // (a tile can ask to keep its message instead: overrides.empty = 'message'). A tile that is WAITING for a choice (a class,
  // a student: meta.hold) always keeps its message — hiding it would leave the viewer with a blank Board and no clue why.
  const hasNoData = (tile) => {
    const meta = results?.[tile.id]?.data?.meta
    // a class/school view with one student picked can never answer there: it leaves, like a tile with no data
    if (meta?.holdKind === 'scope') return true
    return meta?.empty === true && !meta?.hold && tile.overrides?.empty !== 'message'
  }
  const loaded = Boolean(results)
  const tiles = loaded ? compactTiles(allTiles.filter((t) => !hasNoData(t))).sort(byReadingOrder) : allTiles
  const everythingEmpty = loaded && allTiles.length > 0 && tiles.length === 0
  const needsChild = isParentOnly && !childId
  const needsStudent = isStaff && Boolean(board.settings?.studentFirst) && !pickedStudent

  return (
    <BoardStyleContext.Provider value={board.settings || {}}>
      <Box sx={{ p: { xs: 2, md: 3 }, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 2, flexWrap: 'wrap', mb: 2 }}>
          <Box sx={{ flex: '1 1 260px', minWidth: 0 }}>
            <Typography sx={{ fontSize: 22, fontWeight: 700, color: SUMMARY_PALETTE.ink }}>
              {board.name}
            </Typography>
            {board.description ? (
              <Typography sx={{ fontSize: 13, color: SUMMARY_PALETTE.muted }}>{board.description}</Typography>
            ) : null}
          </Box>
          {isParentOnly && children.length > 1 ? (
            <TextField
              select
              size='small'
              label='Student'
              value={childId}
              onChange={(e) => {
                setResults(null)
                setChildId(e.target.value)
              }}
              sx={{ minWidth: 220 }}
            >
              {children.map((c) => (
                <MenuItem key={c._id} value={String(c._id)}>
                  {c.fullName || c.name || c.code}
                </MenuItem>
              ))}
            </TextField>
          ) : null}
          {isStaff ? (
            <StudentSelect
              value={pickedStudent}
              onChange={(o) => {
                setResults(null)
                setPickedStudent(o)
              }}
              area='view'
            />
          ) : null}
          {canBuild ? (
            <Button
              size='small'
              variant='outlined'
              startIcon={<FontAwesomeIcon icon={['fal', 'pencil']} />}
              onClick={() => navigate(`/dashboard/analytics/board-builder/${board._id}`)}
            >
              Edit board
            </Button>
          ) : null}
          {asOf ? (
            <Typography sx={{ fontSize: 12, color: SUMMARY_PALETTE.faint }}>Data as of {asOf}</Typography>
          ) : null}
        </Box>

        {needsStudent ? (
          <Typography sx={{ color: SUMMARY_PALETTE.muted, py: 4 }}>
            Choose a student to see their dashboard.
          </Typography>
        ) : needsChild ? (
          <Typography sx={{ color: SUMMARY_PALETTE.muted, py: 4 }}>
            {children.length
              ? 'Choose a student to see their board.'
              : 'There are no students linked to your account.'}
          </Typography>
        ) : everythingEmpty ? (
          <Typography sx={{ color: SUMMARY_PALETTE.muted, py: 4 }}>
            There is no data to show for this selection.
          </Typography>
        ) : tiles.length === 0 ? (
          <Typography sx={{ color: SUMMARY_PALETTE.muted, py: 4 }}>This board has no tiles yet.</Typography>
        ) : (
          <Box
            sx={{
              display: 'grid',
              gap: `${GRID_GAP}px`,
              gridTemplateColumns: stacked ? 'minmax(0, 1fr)' : 'repeat(12, minmax(0, 1fr))',
              gridAutoRows: `${ROW_HEIGHT}px`
            }}
          >
            {tiles.map((tile) => (
              <Box
                key={tile.id}
                sx={{
                  minWidth: 0,
                  minHeight: 0,
                  overflow: 'hidden',
                  gridColumn: stacked ? '1 / -1' : `${tile.x + 1} / span ${tile.w}`,
                  gridRow: stacked ? `span ${Math.max(tile.h, 2)}` : `${tile.y + 1} / span ${tile.h}`
                }}
              >
                <BoardTile
                  boardCode={board.code}
                  tile={tile}
                  studentId={childId || undefined}
                  result={
                    results
                      ? results[tile.id] || {
                          loading: false,
                          data: null,
                          error: 'This tile could not be loaded.'
                        }
                      : { loading: true, data: null, error: null }
                  }
                />
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </BoardStyleContext.Provider>
  )
}

export default BoardViewer
